import { type ButtonHTMLAttributes, type ReactElement } from 'react';

import { hapticImpact } from '@/lib/haptic';
import { cn } from '@/lib/utils';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'warning' | 'outline' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  haptic?: boolean;
}

const variants = {
  primary: 'action-primary',
  secondary: 'studio-button-secondary',
  outline: 'border border-border bg-transparent text-foreground',
  ghost: 'bg-transparent text-foreground hover:bg-secondary/80',
  destructive: 'bg-destructive text-white',
  accent: 'studio-button-accent',
  warning: 'border border-warning/55 bg-warning/25 text-warning hover:bg-warning/35',
};

const sizes = {
  sm: 'h-11 min-h-[2.75rem] px-3 text-[13px]',
  md: 'h-11 min-h-[2.75rem] px-4 text-sm',
  lg: 'h-12 min-h-[3rem] px-5 text-[15px] font-semibold',
};

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  disabled,
  haptic = true,
  children,
  onClick,
  ...props
}: ButtonProps): ReactElement {
  return (
    <button
      className={cn(
        'studio-button pressable inline-flex items-center justify-center rounded-[var(--radius-md)] font-medium transition-colors disabled:pointer-events-none disabled:opacity-45',
        variants[variant],
        sizes[size],
        className,
      )}
      disabled={disabled}
      onClick={(event) => {
        if (haptic && !disabled) {
          hapticImpact(variant === 'primary' ? 'medium' : 'light');
        }
        onClick?.(event);
      }}
      {...props}
    >
      {children}
    </button>
  );
}
